/**
 * PostCard — the core feed unit. Handles every post type, spoiler gating,
 * book attachments, polls, comments, and moderator-only actions.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Heart, MessageCircle, Bookmark, Share2, Pin, Lock, Unlock, Trash2, MoreVertical, Send, ShieldAlert,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { useCommunities } from '../context/CommunitiesContext';
import { POST_TYPE_LABELS, ROLE_LABELS } from '../constants';
import { timeAgo } from '../utils/communityHelpers';
import { COMMUNITIES } from '@/constants/testIds';
import BookAttachmentCard from './BookAttachmentCard';
import SpoilerGate from './SpoilerGate';

const avatarTone = { coral: 'bg-coral', yellow: 'bg-yellow', sage: 'bg-sage', blue: 'bg-blue' };

function Avatar({ person, size = 'h-9 w-9 text-sm' }) {
  return (
    <span className={cx('grid shrink-0 place-items-center rounded-full font-display font-bold text-[#FFF7F0]', size, avatarTone[person.tone] ?? avatarTone.coral)}>
      {person.initial}
    </span>
  );
}

function Poll({ poll }) {
  const [voted, setVoted] = useState(null);
  const total = poll.options.reduce((sum, o) => sum + o.votes, 0) + (voted !== null ? 1 : 0);

  return (
    <div className="mt-3 space-y-2">
      {poll.options.map((opt, i) => {
        const votes = opt.votes + (voted === i ? 1 : 0);
        const pct = total ? Math.round((votes / total) * 100) : 0;
        return (
          <button
            key={opt.label}
            type="button"
            disabled={voted !== null}
            onClick={() => setVoted(i)}
            className="relative w-full overflow-hidden rounded-lg border border-line bg-cream px-3 py-2 text-left text-sm disabled:cursor-default"
          >
            {voted !== null && (
              <span className="absolute inset-y-0 left-0 bg-coral/15" style={{ width: `${pct}%` }} aria-hidden="true" />
            )}
            <span className="relative flex items-center justify-between">
              <span className="text-ink">{opt.label}</span>
              {voted !== null && <span className="font-semibold text-coral">{pct}%</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function CommentRow({ comment }) {
  const [spoilerRevealed, setSpoilerRevealed] = useState(!comment.isSpoiler);
  return (
    <div className="flex items-start gap-2.5">
      <Avatar person={comment.author} size="h-7 w-7 text-xs" />
      <div className="min-w-0 flex-1 rounded-2xl rounded-tl-md bg-cream px-3.5 py-2.5">
        <p className="text-xs font-semibold text-ink">{comment.author.name} <span className="ml-1.5 font-normal text-subtle">{timeAgo(comment.createdAt)}</span></p>
        {spoilerRevealed ? (
          <p className="mt-0.5 text-sm text-ink">{comment.body}</p>
        ) : (
          <button type="button" onClick={() => setSpoilerRevealed(true)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-coral hover:underline">
            <ShieldAlert className="h-3 w-3" /> Spoiler — reveal comment
          </button>
        )}
      </div>
    </div>
  );
}

export default function PostCard({ post, communityRole }) {
  const { toggleLike, toggleSave, addComment, togglePin, toggleLock, removePost } = useCommunities();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSpoiler, setCommentSpoiler] = useState(false);
  const isModerator = communityRole === 'admin' || communityRole === 'moderator';

  const handleComment = () => {
    if (!commentText.trim()) return;
    addComment(post.id, commentText.trim(), commentSpoiler);
    setCommentText('');
    setCommentSpoiler(false);
  };

  const body = (
    <>
      {post.title && <h3 className="font-display text-lg font-semibold leading-snug text-ink">{post.title}</h3>}
      {post.body && <p className="mt-1.5 text-sm leading-relaxed text-ink/90">{post.body}</p>}
      {post.excerpt && (
        <blockquote className="mt-3 rounded-xl border-l-2 border-coral bg-cream px-4 py-3 font-display text-[15px] italic leading-relaxed text-ink">
          “{post.excerpt}”
        </blockquote>
      )}
      {post.book && <BookAttachmentCard book={post.book} />}
      {post.poll && <Poll poll={post.poll} />}
    </>
  );

  return (
    <motion.article
      layout="position" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
      className="card-paper p-5" data-testid={COMMUNITIES.post}
    >
      <div className="flex items-start gap-3">
        <Avatar person={post.author} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
            <span className="font-semibold text-ink">{post.author.name}</span>
            {post.author.role && post.author.role !== 'member' && (
              <span className="rounded-full bg-coral/10 px-1.5 py-0.5 text-[10px] font-semibold text-coral">{ROLE_LABELS[post.author.role]}</span>
            )}
            <span className="text-subtle">· {timeAgo(post.createdAt)}</span>
            {post.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-coral"><Pin className="h-3 w-3" /> Pinned</span>}
            {post.locked && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-subtle"><Lock className="h-3 w-3" /> Locked</span>}
          </div>
          <span className="mt-1 inline-block rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-subtle">
            {POST_TYPE_LABELS[post.type] ?? 'Discussion'}
          </span>
        </div>

        {isModerator && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="rounded-full p-1.5 text-subtle transition-colors hover:bg-cream hover:text-ink" aria-label="Moderation options">
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl border-line bg-card p-1.5 text-ink shadow-xl">
              <DropdownMenuItem onSelect={() => togglePin(post.id)} className="cursor-pointer gap-2.5 rounded-lg text-sm">
                <Pin className="h-3.5 w-3.5 text-subtle" /> {post.pinned ? 'Unpin Post' : 'Pin Post'}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => toggleLock(post.id)} className="cursor-pointer gap-2.5 rounded-lg text-sm">
                {post.locked ? <Unlock className="h-3.5 w-3.5 text-subtle" /> : <Lock className="h-3.5 w-3.5 text-subtle" />}
                {post.locked ? 'Unlock Discussion' : 'Lock Discussion'}
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-line" />
              <DropdownMenuItem onSelect={() => { removePost(post.id); toast('Post removed'); }} className="cursor-pointer gap-2.5 rounded-lg text-sm font-medium text-coral focus:bg-coral/10 focus:text-coral">
                <Trash2 className="h-3.5 w-3.5" /> Remove Post
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="mt-3">
        {post.isSpoiler ? <SpoilerGate>{body}</SpoilerGate> : body}
      </div>

      <div className="mt-4 flex items-center gap-1 border-t border-line pt-3 text-subtle">
        <button
          type="button" onClick={() => toggleLike(post.id)}
          className={cx('flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-cream', post.liked ? 'text-coral' : 'hover:text-ink')}
        >
          <Heart className={cx('h-4 w-4', post.liked && 'fill-coral text-coral')} /> {post.likes}
        </button>
        <button
          type="button" onClick={() => setShowComments(v => !v)}
          className="flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-cream hover:text-ink"
        >
          <MessageCircle className="h-4 w-4" /> {post.comments.length}
        </button>
        <button
          type="button" onClick={() => toggleSave(post.id)}
          className={cx('flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-cream', post.saved ? 'text-coral' : 'hover:text-ink')}
        >
          <Bookmark className={cx('h-4 w-4', post.saved && 'fill-coral text-coral')} />
        </button>
        <button
          type="button" onClick={() => toast('Link copied')}
          className="ml-auto flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-cream hover:text-ink"
        >
          <Share2 className="h-4 w-4" />
        </button>
      </div>

      {showComments && (
        <div className="mt-3 space-y-2.5 border-t border-line pt-3">
          {post.comments.map(c => <CommentRow key={c.id} comment={c} />)}

          {post.locked ? (
            <p className="text-xs text-subtle">This discussion is locked.</p>
          ) : (
            <div className="flex items-center gap-2">
              <input
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleComment()}
                placeholder="Add a comment…"
                className="flex-1 rounded-full border border-line bg-cream px-3.5 py-2 text-sm text-ink outline-none focus:border-coral"
              />
              <label className="flex shrink-0 items-center gap-1 text-[11px] text-subtle">
                <input type="checkbox" checked={commentSpoiler} onChange={e => setCommentSpoiler(e.target.checked)} /> Spoiler
              </label>
              <button type="button" onClick={handleComment} className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-coral-fill text-[#FFF7F0]" aria-label="Post comment">
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </motion.article>
  );
}
